import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import * as Sentry from "@sentry/react";
import { posthog } from "@/lib/posthog";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  admin: boolean;
  super_admin: boolean;
  viewer: boolean;
  client_id: string | null;
  enabled: boolean;
  pii_enabled?: boolean;
  hourly_unmask_limit?: number;
  download_allowed?: boolean;
  daily_download_limit?: number;
  has_data?: boolean;
}

interface ClientInfo {
  id: string;
  name: string;
  enabled: boolean;
  claude_api_key: string | null;
  logo_url: string | null;
  urlslug: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  clientInfo: ClientInfo | null;
  loading: boolean;
  ipBlocked: boolean;
  signOut: () => Promise<void>;
  markHasData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  clientInfo: null,
  loading: true,
  ipBlocked: false,
  signOut: async () => {},
  markHasData: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [ipBlocked, setIpBlocked] = useState(false);

  const checkIpWhitelist = async (accessToken: string) => {
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-ip-whitelist`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        setIpBlocked(!data.allowed);
      } else {
        // On error, don't block (fail open)
        setIpBlocked(false);
      }
    } catch {
      // Network error — don't block
      setIpBlocked(false);
    }
  };

  const fetchProfile = async (userId: string, accessToken?: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, full_name, avatar_url, admin, super_admin, viewer, client_id, enabled, pii_enabled, hourly_unmask_limit, download_allowed, has_data")
      .eq("id", userId)
      .single();
    setProfile(data ?? null);

    // Fetch client info if profile has a client_id
    if (data?.client_id) {
      const { data: client } = await supabase
        .from("client")
        .select("id, name, enabled, claude_api_key, logo_url, urlslug")
        .eq("id", data.client_id)
        .single();
      setClientInfo(client ?? null);

      // Persist the urlslug in a shared .databull.ai cookie so that
      // app.databull.ai can redirect back to the correct subdomain when the
      // user has no session there (e.g. opened a new tab on app domain).
      if (client?.urlslug) {
        const { hostname } = window.location;
        if (hostname.endsWith(".databull.ai") || hostname === "databull.ai") {
          document.cookie = `dfg_urlslug=${client.urlslug}; domain=.databull.ai; path=/; SameSite=Lax; Secure; max-age=31536000`;
        }
      }
    } else {
      setClientInfo(null);
    }

    // Check IP whitelist for non-super-admin users
    if (data && !data.super_admin && data.client_id && accessToken) {
      await checkIpWhitelist(accessToken);
    } else {
      setIpBlocked(false);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id, email: session.user.email });
        posthog.identify(session.user.id, { email: session.user.email });
        fetchProfile(session.user.id, session.access_token);
      } else {
        Sentry.setUser(null);
        posthog.reset();
        setProfile(null);
        setClientInfo(null);
        setIpBlocked(false);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        await fetchProfile(session.user.id, session.access_token);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Clear the shared urlslug cookie so app.databull.ai does not redirect
    // to this user's subdomain after they log out.
    const { hostname } = window.location;
    if (hostname.endsWith(".databull.ai") || hostname === "databull.ai") {
      document.cookie = "dfg_urlslug=; domain=.databull.ai; path=/; max-age=0";
    }
    // If logging out from a urlslug subdomain, also clear the stale
    // localStorage session on app.databull.ai.
    if (hostname.endsWith(".databull.ai")) {
      const sub = hostname.slice(0, hostname.length - ".databull.ai".length);
      if (sub && sub !== "app") {
        window.location.href = "https://app.databull.ai/signout";
      }
    }
  };

  const markHasData = async () => {
    if (!profile || profile.has_data) return;
    setProfile((prev) => prev ? { ...prev, has_data: true } : prev);
    await supabase
      .from("profiles")
      .update({ has_data: true } as any)
      .eq("id", profile.id);
  };

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, profile, clientInfo, loading, ipBlocked, signOut, markHasData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
